import type { QueryClient } from "@tanstack/react-query"
import type { User } from "@auth0/auth0-react"
import type { MyCustomersResponse } from "api/useMyCustomers"
import type { Profile } from "api/useAuth0Metadata"
import { seedBootCaches } from "api/bootstrapQuery"
import { isLoginCallback } from "auth/loginCallback"
import type { Customer, CustomerId } from "types/customer"

// Boot snapshot: what the last session in this browser booted with — the
// signed-in user's claims, the /me/bootstrap parts and the customer it
// opened. A reload renders the app from it straight away (no splash) while
// Auth0 and GET /me/bootstrap revalidate everything behind it; the server's
// answer wins wherever it differs (CustomerProvider).
//
// Only ever used when the Auth0 SDK's own "a session exists" cookie is
// present, cleared on logout, and discarded when a different account signs
// in. Nothing in it grants access: every request still needs a token, and
// the snapshot only holds what this browser was already shown — though that
// includes the profile (name, email, UI preferences) and the customer config
// (e.g. SRR email lists), in localStorage like the Auth0 tokens themselves.

export const BOOT_SNAPSHOT_STORAGE_KEY = "rr.boot-snapshot"
export const BOOT_SNAPSHOT_VERSION = 1
// Past this the snapshot is more likely to mislead (renamed menus, revoked
// customers) than to help: boot the slow way and write a fresh one.
export const BOOT_SNAPSHOT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

export type BootSnapshot = {
  v: typeof BOOT_SNAPSHOT_VERSION
  // the app build that wrote it: another build may read its shapes differently
  build: string
  savedAt: number
  sub: string
  // the Auth0 user (ID token claims) useAuth0() served at the time
  user: User
  customerId: CustomerId
  profile: Profile
  myCustomers: MyCustomersResponse
  customer: Customer
}

type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">

const browserStorage = (): Storage | null => {
  try {
    return window.localStorage ?? null
  } catch {
    // blocked storage (privacy settings, sandboxed frames)
    return null
  }
}

type Readable = Pick<Storage, "getItem">

// release tag / commit (vite define); empty in tests
const currentBuild = () => (typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "")


/**
 * Whether the Auth0 SPA SDK believes a session exists — without one a reload
 * goes to the login page, so a snapshot would only flash a stale app. The SDK
 * keeps two markers and removes both on logout: the
 * `auth0.<clientId>.is.authenticated` cookie (Secure, so absent on plain-http
 * localhost) and, with `cacheLocation: "localstorage"`, its cached user.
 */
/**
 * The account the Auth0 SDK has cached (cacheLocation "localstorage": its
 * `@@user@@` entry is `{ id_token, decodedToken: { claims, user } }`), or
 * null when it has none or can't be read.
 */
export const sdkCachedSub = ({ storage, clientId }: { storage: Readable | null; clientId: string }): string | null => {
  try {
    const raw = storage?.getItem(`@@auth0spajs@@::${clientId}::@@user@@`)
    if (!raw) return null
    const entry = JSON.parse(raw) as { decodedToken?: { claims?: { sub?: unknown }; user?: { sub?: unknown } } }
    const sub = entry?.decodedToken?.claims?.sub ?? entry?.decodedToken?.user?.sub
    return typeof sub === "string" ? sub : null
  } catch {
    return null
  }
}

export const hasAuth0SessionHint = ({ cookie, storage, clientId }: { cookie: string; storage: Readable | null; clientId: string }) => {
  if (cookie.split(";").some((part) => part.trim() === `auth0.${clientId}.is.authenticated=true`)) return true
  try {
    return !!storage?.getItem(`@@auth0spajs@@::${clientId}::@@user@@`)
  } catch {
    return false
  }
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value)

const isSnapshot = (value: unknown): value is BootSnapshot => {
  if (!isObject(value) || value.v !== BOOT_SNAPSHOT_VERSION) return false
  if (typeof value.build !== "string" || typeof value.savedAt !== "number" || typeof value.sub !== "string" || typeof value.customerId !== "string") return false
  if (!isObject(value.user) || value.user.sub !== value.sub) return false
  if (!isObject(value.customer) || !isObject(value.profile)) return false
  const { myCustomers } = value
  if (!isObject(myCustomers) || !Array.isArray(myCustomers.customers)) return false
  // the snapshot's customer must be one its own list allowed
  return myCustomers.customers.some((entry) => isObject(entry) && entry.customer_id === value.customerId)
}

type ReadOptions = {
  clientId: string
  build?: string
  search?: string
  cookie?: string
  storage?: Storage | null
  now?: number
  // the customer the URL asks for (?customer= link, dev pin): a snapshot of
  // another customer would render the wrong app until the switch
  preferredCustomer?: CustomerId | null
}

export const readBootSnapshot = ({
  clientId,
  cookie = typeof document === "undefined" ? "" : document.cookie,
  storage = browserStorage(),
  now = Date.now(),
  preferredCustomer = null,
  build = currentBuild(),
  search = typeof window === "undefined" ? "" : window.location.search,
}: ReadOptions): BootSnapshot | null => {
  if (!storage || !hasAuth0SessionHint({ cookie, storage, clientId })) return null
  // Straight back from the login page: whoever just signed in may not be the
  // snapshot's account, and the SDK only knows once it has handled the code.
  if (isLoginCallback(search)) return null
  let parsed: unknown
  try {
    const raw = storage.getItem(BOOT_SNAPSHOT_STORAGE_KEY)
    if (!raw) return null
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!isSnapshot(parsed) || parsed.build !== build) return null
  // Another account's snapshot (it signed out without the Logout button and
  // someone else signed in here) must not render, even for a moment.
  const cachedSub = sdkCachedSub({ storage, clientId })
  if (cachedSub && cachedSub !== parsed.sub) return null
  if (now - parsed.savedAt > BOOT_SNAPSHOT_MAX_AGE_MS || parsed.savedAt > now + 60_000) return null
  if (preferredCustomer && preferredCustomer !== parsed.customerId) return null
  return parsed
}

export const writeBootSnapshot = (snapshot: Omit<BootSnapshot, "v" | "build">, storage: Storage | null = browserStorage(), build = currentBuild()) => {
  if (!storage) return
  try {
    storage.setItem(BOOT_SNAPSHOT_STORAGE_KEY, JSON.stringify({ v: BOOT_SNAPSHOT_VERSION, build, ...snapshot }))
  } catch {
    // quota / blocked storage: the next boot just takes the slow path
  }
}

export const clearBootSnapshot = (storage: Storage | null = browserStorage()) => {
  try {
    storage?.removeItem(BOOT_SNAPSHOT_STORAGE_KEY)
  } catch {
    // nothing to clear
  }
}

/**
 * Seed the query cache from a snapshot, dated when it was taken: the hooks
 * render from it at once, and GET /me/bootstrap replaces it when it answers.
 */
export const hydrateFromBootSnapshot = (queryClient: QueryClient, snapshot: BootSnapshot) => {
  seedBootCaches(
    queryClient,
    { profile: snapshot.profile, customers: snapshot.myCustomers, customerId: snapshot.customerId, customer: snapshot.customer },
    snapshot.savedAt,
  )
}
